import { useCallback, useEffect, useMemo, useState } from 'react';
import SettingsMainLayout from 'components/organisms/settings/MainLayoutV2';
import {
  useServiceForm,
  useSettingsForm,
  useSettingsMainForm,
} from 'hooks/formStates/settings';
import routeCodes from 'common/constants/route-codes';
import { useCommon } from 'redux/common/use-common';
import SettingsApi from 'libs/api/settings';
import UIHelper from 'services/UIHelper';
import { useHistory, useParams } from 'react-router-dom';
import ServicesApi from 'libs/api/services';
import ServiceForm from 'components/molecules/settings/v2/ServiceForm';
import { nanoid } from 'nanoid';
import RouterHelper from 'services/RouterHelper';
import { useUser } from 'redux/user/use-user';

const SettingsV2ServiceCreate = ({ history }) => {
  const [localUpdateInProcess, setLocalUpdateInProcess] = useState(false);
  const [openingHourErrors, setOpeningHourErrors] = useState(null);
  const [inProcess, setInProcess] = useState(false);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const { isSuperAdmin } = useUser();

  const {
    localState: mainState,
    onSelectConfig,
    onChange: mainFormChange,
    dataForRequest,
    onSubmit: mainFormSubmit,
  } = useSettingsMainForm(true);

  const onCancel = useCallback(() => {
    history.push(routeCodes.home);
  }, [history]);

  const {
    localState,
    onSelectTime,
    onChange,
    onClickChip,
    onChangeServiceRoom,
    onChangeRoomsCount,
    onClickRoomsCount,
    onChangeRoomItem,
    errors,
    setErrors,
    rooms,
    onChangeRoom,
    onClickAddRoom,
    setLocalState,
    onDeleteException,
  } = useServiceForm(true);

  const {
    deliveryDates,
    deliveryActiveTab,
    fetchDeliveryDates,
    changeDeliveryDayConfig,
    onChangeDeliveryTab,
    setHoursErrors,
    onChangeMaximum,
    onChangeExceptionDay,
    onAddExceptionData,
    isLoading,
    setDeliveryDates,
  } = useSettingsForm();
  const { currentRestaurant } = useCommon();

  const settingsApi = useMemo(() => {
    return new SettingsApi(currentRestaurant?.id);
  }, [currentRestaurant]);

  const api = useMemo(() => {
    return new ServicesApi(currentRestaurant?.id);
  }, [currentRestaurant]);

  const onSubmit = useCallback(async () => {
    setInProcess(true);
    try {
      let hourErrors = false;
      try {
        await settingsApi.updateOpeningHours(
          deliveryActiveTab,
          deliveryDates[deliveryActiveTab],
        );
        const { data: newData } = await settingsApi.get(
          `opening_hours/${deliveryActiveTab}`,
        );
        setDeliveryDates((state) => ({
          ...state,
          [deliveryActiveTab]: {
            ...state[deliveryActiveTab],
            isFetched: true,
            errors: null,
            ...newData,
          },
        }));
      } catch (e) {
        hourErrors = true;
        setHoursErrors(e?.response?.data?.opening_hours);
        setOpeningHourErrors(e?.response?.data?.opening_hours);
      }
      if (hourErrors) return;
      await api.put(id, {
        data: {
          service:{
            ...localState,
          }
        },
      });
      if (Object.keys(dataForRequest)?.length) {
        mainFormSubmit(dataForRequest);
      } else {
        UIHelper.showSuccessMessage();
        history.push(
          isSuperAdmin
            ? RouterHelper.getRouteWithRestaurant(
                routeCodes.superAdmin.settings,
              )
            : routeCodes.settings,
        );
      }
    } catch (e) {
      setErrors(e?.response?.data);
    } finally {
      setInProcess(false);
    }
  }, [
    id,
    api,
    settingsApi,
    isSuperAdmin,
    localState,
    history,
    dataForRequest,
    mainFormSubmit,
    deliveryActiveTab,
    deliveryDates,
  ]);

  const handleUpdateSettingTimes = useCallback(async () => {
    setLocalUpdateInProcess(true);
    try {
      await settingsApi.updateOpeningHours(
        deliveryActiveTab,
        deliveryDates[deliveryActiveTab],
      );
      UIHelper.showCreateOrUpdateSuccess();
    } catch (e) {
      setOpeningHourErrors(e?.response?.data?.opening_hours);
    } finally {
      setLocalUpdateInProcess(false);
    }
  }, [deliveryActiveTab, dataForRequest, deliveryDates]);

  useEffect(() => {
    if (!currentRestaurant?.id) return;
    const currentlyApi = new ServicesApi(currentRestaurant.id);
    (async () => {
      try {
        const {
          data: {
            service: { service_opening_hours, services_rooms, ...rest },
          },
        } = await currentlyApi.get(id);
        setLocalState({
          ...rest,
          service_opening_hours_attributes: service_opening_hours,
          services_rooms_attributes: [
            ...(services_rooms || []),
            {
              room_id: '',
              total_seats: '',
              uuid: nanoid(),
            },
          ],
        });
      } catch (e) {
      } finally {
        setLoading(false);
      }
    })();
  }, [id, currentRestaurant]);

  return (
    <SettingsMainLayout
      setOpeningHourErrors={setOpeningHourErrors}
      openingHourErrors={openingHourErrors}
      changeDeliveryDayConfig={changeDeliveryDayConfig}
      deliveryActiveTab={deliveryActiveTab}
      deliveryDates={deliveryDates}
      fetchDeliveryDates={fetchDeliveryDates}
      onChangeDeliveryTab={onChangeDeliveryTab}
      setHoursErrors={setHoursErrors}
      onChangeMaximum={onChangeMaximum}
      onChangeExceptionDay={onChangeExceptionDay}
      onAddExceptionData={onAddExceptionData}
      isLoading={isLoading}
      onSubmitAction={onSubmit}
      onSelectConfig={onSelectConfig}
      showFooter={true}
      localState={mainState}
      localUpdateInProcess={inProcess}
      onChange={mainFormChange}
      onCancel={onCancel}
      onDeleteException={onDeleteException}
      isSuperAdmin={isSuperAdmin}
      setDeliveryDates={setDeliveryDates}
    >
      {/*<Divider style={{ width: '100%' }} />*/}
      <ServiceForm
        rooms={rooms}
        onChangeRoom={onChangeRoom}
        localState={localState}
        onSelectTime={onSelectTime}
        onChange={onChange}
        onClickChip={onClickChip}
        onChangeServiceRoom={onChangeServiceRoom}
        onChangeRoomsCount={onChangeRoomsCount}
        onClickRoomsCount={onClickRoomsCount}
        onChangeRoomItem={onChangeRoomItem}
        onClickAddRoom={onClickAddRoom}
        errors={errors}
      />
    </SettingsMainLayout>
  );
};

export default SettingsV2ServiceCreate;
